import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "../../../components/dashboard-layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Progress } from "../../../components/ui/progress";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { useTheme } from "../../../contexts/theme-provider";
import { useAuth } from "../../../contexts/auth-context";
import {
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Download,
  Loader2,
  FileText,
  XCircle,
  Sparkles,
} from "lucide-react";
import { downloadInputFile } from "../../../services/reports";

interface BulkProcessingResult {
  index: number;
  filename: string;
  status: "pending" | "processing" | "success" | "failed";
  report_id?: number;
  report_no?: string;
  download_url?: string;
  error?: string;
  blob_name?: string;
}

interface BulkReportData {
  message: string;
  total_files: number;
  successful: number;
  failed: number;
  results: BulkProcessingResult[];
}

export default function BulkReportProcessingPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bulkData, setBulkData] = useState<BulkReportData | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [, setDownloadingFiles] = useState<Set<number>>(new Set());
  const [downloadingInputs, setDownloadingInputs] = useState<Set<number>>(
    new Set(),
  );
  const [showAll, setShowAll] = useState(false);

  const normalizeResult = (r: any, index: number): BulkProcessingResult => ({
    index: r.index ?? index,
    filename: r.filename || r.fileName || "Unknown file",
    status:
      r.status === "completed"
        ? "success"
        : r.status === "success"
          ? "success"
          : r.status === "failed"
            ? "failed"
            : "processing",
    report_id: r.report_id || r.id,
    report_no: r.report_no || r.reportNo,
    download_url: r.download_url || r.downloadUrl,
    error: r.error,
    blob_name: r.blob_name || r._debug?.input_file_blob,
  });

  useEffect(() => {
    const data =
      location.state?.bulkData || sessionStorage.getItem("bulkProcessingData");

    if (data) {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      const normalizedResults = (parsedData.results || []).map(
        (r: any, index: number) => normalizeResult(r, index),
      );

      const finalData: BulkReportData = {
        message: parsedData.message || "Bulk processing completed successfully",
        total_files: parsedData.total_files || normalizedResults.length,
        successful:
          parsedData.successful ??
          normalizedResults.filter((r: any) => r.status === "success").length,
        failed:
          parsedData.failed ??
          normalizedResults.filter((r: any) => r.status === "failed").length,
        results: normalizedResults,
      };

      setBulkData(finalData);

      const allProcessed = normalizedResults.every(
        (r: any) => r.status === "success" || r.status === "failed",
      );

      if (allProcessed) {
        setIsComplete(true);
      }
    } else {
      navigate("/user/dashboard");
    }
  }, [navigate, location.state]);

  const handleDownload = async (
    result: BulkProcessingResult,
    index: number,
  ) => {
    if (!result.download_url) return;

    setDownloadingFiles((prev) => new Set(prev).add(index));

    try {
      const link = document.createElement("a");
      link.href = result.download_url;
      link.setAttribute(
        "download",
        `${result.filename.replace(".xlsx", "")}_output.docx`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleDownloadInput = async (
    result: BulkProcessingResult,
    index: number,
  ) => {
    if (!result.report_id || !user?.access_token) return;

    setDownloadingInputs((prev) => new Set(prev).add(index));

    try {
      await downloadInputFile(
        result.report_id.toString(),
        result.filename,
        user.access_token,
      );

      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadingInputs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleDownloadAll = async () => {
    if (!bulkData) return;

    const successfulResults = bulkData.results.filter(
      (r) => r.status === "success" && r.download_url,
    );

    for (const [index, result] of successfulResults.entries()) {
      await handleDownload(result, index);
      if (index < successfulResults.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  const handleDownloadAllInputs = async () => {
    if (!bulkData || !user?.access_token) return;

    const successfulResults = bulkData.results.filter(
      (r) => r.status === "success" && r.report_id,
    );

    for (const [index, result] of successfulResults.entries()) {
      await handleDownloadInput(result, index);
      if (index < successfulResults.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  if (!bulkData) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const successCount =
    bulkData.successful ??
    bulkData.results.filter((r) => r.status === "success").length;

  const failedCount =
    bulkData.failed ??
    bulkData.results.filter((r) => r.status === "failed").length;

  const overallProgress =
    bulkData.total_files > 0
      ? ((successCount + failedCount) / bulkData.total_files) * 100
      : 0;

  const displayResults = showAll
    ? bulkData.results
    : bulkData.results.slice(0, 5);

  return (
    <DashboardLayout>
      <div
        className={`p-4 md:p-8 min-h-screen ${
          theme === "dark"
            ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
            : "bg-gradient-to-br from-slate-50 via-white to-blue-50"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-4 max-w-4xl mx-auto"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="w-full sm:w-auto">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`text-2xl md:text-3xl font-black bg-clip-text text-transparent ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-white via-blue-100 to-purple-100"
                    : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
                }`}
              >
                Bulk Processing
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`font-medium mt-1 text-sm ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
              >
                {bulkData.message}
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full sm:w-auto"
            >
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center"
                >
                  <Sparkles className="h-3 w-3 text-white" />
                </motion.div>
                <span
                  className={`text-sm font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
                >
                  {bulkData.total_files} files
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Stats Cards - Only Completed and Failed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="grid grid-cols-2 gap-3"
          >
            {[
              {
                icon: CheckCircle,
                label: "Completed",
                value: successCount,
                color: "from-green-500 to-emerald-600",
                bgColor:
                  theme === "dark"
                    ? "from-green-950/30 to-emerald-950/30"
                    : "from-green-50 dark:from-green-900/30 to-emerald-50 dark:to-emerald-900/30",
              },
              {
                icon: XCircle,
                label: "Failed",
                value: failedCount,
                color: "from-red-500 to-orange-600",
                bgColor:
                  theme === "dark"
                    ? "from-red-950/30 to-orange-950/30"
                    : "from-red-50 dark:from-red-900/30 to-orange-50 dark:to-orange-900/30",
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
              >
                <Card
                  className={`bg-gradient-to-br ${stat.bgColor} border-0 shadow-sm h-full`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className={`text-xs font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} mb-1`}
                        >
                          {stat.label}
                        </p>
                        <p
                          className={`text-2xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                        >
                          {stat.value}
                        </p>
                      </div>
                      <div
                        className={`w-8 h-8 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center shadow-sm`}
                      >
                        <stat.icon className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Overall Progress Bar */}
          <Card
            className={`border shadow-sm ${theme === "dark" ? "bg-slate-800/70 border-slate-700/50" : "bg-white/70 border-slate-200/50"}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1.5">
                    <span
                      className={`text-xs font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
                    >
                      Overall Progress
                    </span>
                    <span
                      className={`text-xs font-bold ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
                    >
                      {Math.round(overallProgress)}%
                    </span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </div>

                <div className="flex gap-2">
                  {isComplete && successCount > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={handleDownloadAll}
                        className="h-8 text-xs bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download All Outputs ({successCount})
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleDownloadAllInputs}
                        className="h-8 text-xs bg-green-600 hover:bg-green-700 whitespace-nowrap"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                        Download All Inputs ({successCount})
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Table/Cards */}
          <Card
            className={`backdrop-blur-xl shadow-lg ${theme === "dark" ? "bg-slate-800/70 border-slate-700/50" : "bg-white/70 border-slate-200/50"}`}
          >
            <CardHeader className="p-4 pb-2">
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center"
                >
                  <FileText className="h-2.5 w-2.5 text-white" />
                </motion.div>
                <CardTitle
                  className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
                >
                  Files ({bulkData.results.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              <div className="space-y-1.5">
                {displayResults.map((result, index) => (
                  <motion.div
                    key={result.index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`p-2 rounded-lg border ${
                      result.status === "success"
                        ? theme === "dark"
                          ? "bg-green-950/10 border-green-800/30"
                          : "bg-green-50/50 border-green-200"
                        : result.status === "failed"
                          ? theme === "dark"
                            ? "bg-red-950/10 border-red-800/30"
                            : "bg-red-50/50 border-red-200"
                          : result.status === "processing"
                            ? theme === "dark"
                              ? "bg-blue-950/10 border-blue-800/30"
                              : "bg-blue-50/50 border-blue-200"
                            : theme === "dark"
                              ? "bg-slate-800/30 border-slate-700"
                              : "bg-slate-50/50 border-slate-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {result.status === "success" ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                        ) : result.status === "failed" ? (
                          <XCircle className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                        ) : result.status === "processing" ? (
                          <Loader2 className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 animate-spin" />
                        ) : (
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                        )}
                      </div>

                      {/* Filename and Status */}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span
                          className={`text-xs font-medium truncate max-w-[150px] ${
                            theme === "dark"
                              ? "text-slate-200"
                              : "text-slate-700 dark:text-slate-200"
                          }`}
                        >
                          {result.filename}
                        </span>

                        {result.report_no && (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-4 px-1.5"
                          >
                            #{result.report_no}
                          </Badge>
                        )}

                        {result.status === "processing" && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                            Processing
                          </Badge>
                        )}
                      </div>

                      {/* Download Buttons */}
                      {result.status === "success" && (
                        <div className="flex gap-1">
                          {/* Input Download Button - New for XLSX */}
                          {result.report_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadInput(result, index)}
                              disabled={downloadingInputs.has(index)}
                              className={`h-6 px-2 text-xs ${
                                theme === "dark"
                                  ? "hover:bg-green-900/20 text-green-400"
                                  : "hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
                              }`}
                            >
                              {downloadingInputs.has(index) ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <FileSpreadsheet className="h-3 w-3 mr-1" />
                                  Input
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Error Tooltip */}
                      {result.error && (
                        <div className="relative group">
                          <AlertCircle className="h-3.5 w-3.5 text-red-400 cursor-help" />
                          <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-10">
                            <div
                              className={`text-[10px] p-1.5 rounded shadow-lg max-w-[200px] ${
                                theme === "dark"
                                  ? "bg-slate-700 text-slate-200"
                                  : "bg-white text-slate-700 dark:text-slate-200"
                              }`}
                            >
                              {result.error}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Show More/Less */}
                {bulkData.results.length > 5 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                    className={`w-full mt-2 h-7 text-xs ${
                      theme === "dark"
                        ? "text-slate-400 hover:text-white"
                        : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    {showAll
                      ? "Show Less"
                      : `Show ${bulkData.results.length - 5} More Files`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/user/dashboard")}
              className="flex-1 h-8 text-xs"
            >
              Back to Dashboard
            </Button>
            {isComplete && (
              <Button
                size="sm"
                onClick={() => navigate("/user/reports")}
                className="flex-1 h-8 text-xs bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                View All Reports
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
